import { MessageDto, MessageType } from 'common/dto-types/message-dto';
import TournamentDto from 'common/dto-types/tournament-dto';
import UserDto from 'common/dto-types/user-dto';
import UserGroupDto from 'common/dto-types/user-group-dto';
import * as yup from 'yup';
import { getPlainTextLength } from './utils';
import { MessageArticleValidationStatus } from 'components/contentful-article-search-field/dto';

type MessageFormProps = {
    message?: MessageDto;
};

export type MessageFormFields = {
    messageTitle: string;
    messageDescription: string;
    messageType: MessageType | '';
    tournament?: TournamentDto | null;
    promotionMessagePayload?: {
        description: string;
    };
    articleMessagePayload?: {
        articleEntryId: string;
        validationStatus?: MessageArticleValidationStatus;
    };
    userGroups: UserGroupDto[];
    selectedUsers: UserDto[];
};

export const defaultInitialMessageFormValue: MessageFormFields = {
    messageTitle: '',
    messageDescription: '',
    messageType: '',
    tournament: null,
    promotionMessagePayload: {
        description: '',
    },
    articleMessagePayload: {
        articleEntryId: '',
        validationStatus: MessageArticleValidationStatus.Idle,
    },
    userGroups: [],
    selectedUsers: [],
};

// Base validation (always applied)
const baseMessageValidationSchema = yup.object().shape({
    messageTitle: yup
        .string()
        .required('Message title is required')
        .max(30, 'Message title must be less than 30 characters'),
    messageDescription: yup.string().max(150, 'Message description must be less than 150 characters'),
    messageType: yup
        .string()
        .oneOf([MessageType.Promotion, MessageType.Article], 'Message type must be either Promotion or Article')
        .required('Message type is required'),
});

// Upsert validation schema (lenient - allows empty payloads for draft/save)
export const upsertMessageValidationSchema = baseMessageValidationSchema.shape({
    promotionMessagePayload: yup.object().when('messageType', {
        is: MessageType.Promotion,
        then: (schema) =>
            schema.shape({
                description: yup
                    .string()
                    .test(
                        'max-plain-text-length',
                        'Promotion description must be less than 2000 characters. Otherwise, please use Article message type instead.',
                        (value) => {
                            if (!value) return true; // Allow empty for draft
                            return getPlainTextLength(value) <= 2000;
                        },
                    ),
            }),
        otherwise: (schema) => schema.optional(),
    }),
    articleMessagePayload: yup.object().when('messageType', {
        is: MessageType.Article,
        then: (schema) =>
            schema.shape({
                articleEntryId: yup
                    .string()
                    .test(
                        'is-validated',
                        'Please validate article entry ID by fetching preview first',
                        function (value) {
                            if (!value) return true; // Allow empty for draft
                            const validationStatus = this.parent.validationStatus;
                            // For error status: validation fails but we return a different error message
                            if (validationStatus === MessageArticleValidationStatus.Error) {
                                return this.createError({
                                    message: 'Article validation failed. Please check the entry ID and try again.',
                                });
                            }
                            // For validating status: still waiting, should not allow save
                            if (validationStatus === MessageArticleValidationStatus.Validating) {
                                return this.createError({
                                    message: 'Please wait for article validation to complete',
                                });
                            }
                            // For idle or any other status: require validation
                            return validationStatus === MessageArticleValidationStatus.Success;
                        },
                    ),
            }),
        otherwise: (schema) => schema.optional(),
    }),
});

// Test validation schema
export const sendTestMessageValidationSchema = upsertMessageValidationSchema.shape({
    promotionMessagePayload: yup.object().when('messageType', {
        is: MessageType.Promotion,
        then: (schema) =>
            schema.shape({
                description: yup
                    .string()
                    .test(
                        'min-plain-text-length',
                        'Promotion description is required for send test / publishing',
                        (value) => {
                            if (!value) return false; // Required for send test
                            return getPlainTextLength(value) > 0;
                        },
                    )
                    .test(
                        'max-plain-text-length',
                        'Promotion description must be less than 2000 characters. Otherwise, please use Article message type instead.',
                        (value) => {
                            if (!value) return false; // Required for send test
                            return getPlainTextLength(value) <= 2000;
                        },
                    ),
            }),
        otherwise: (schema) => schema.optional(),
    }),
    articleMessagePayload: yup.object().when('messageType', {
        is: MessageType.Article,
        then: (schema) =>
            schema.shape({
                articleEntryId: yup.string().required('Article entry ID is required for send test / publishing'),
            }),
        otherwise: (schema) => schema.optional(),
    }),
});

// Publish validation schema (strict - requires filled payloads)
export const publishMessageValidationSchema = sendTestMessageValidationSchema.shape({
    userGroups: yup
        .array()
        .test('at-least-one-target', 'At least one group or user is required for publishing', function (value) {
            const { selectedUsers } = this.parent;
            const hasGroups = value && value.length > 0;
            const hasUsers = selectedUsers && selectedUsers.length > 0;
            return hasGroups || hasUsers;
        }),
});

export default MessageFormProps;
